import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
  IntercomEventService,
  IntercomEventsByArea,
  RefModelStoreService,
  Refmodel,
  SnackBarService
} from '@q9elements/ui-kit/common';
import {GridStore} from '@q9elements/ui-kit/components';
import {Observable, of} from 'rxjs';
import {filter, switchMap, tap} from 'rxjs/operators';

import {CustomViewDaoService} from '../../../core/services/custom-view-dao.service';
import {GridPagerService} from '../../../custom-grid/services/grid-pager.service';
import {GridSelectorService} from '../../../custom-grid/services/grid-selector.service';
import {ICustomView} from '../../models/custom-view.interface';
import {IDataTypeChangeEvent} from '../../models/data-type-change.interface';
import {TreeListViewEnum} from '../../models/enums/TreeListView.enum';
import {CustomViewService} from '../../services/custom-view.service';
import {EditorInterfaceDialogService} from '../../services/editor-interface-dialog.service';

@Component({
  selector: 'app-view-selector',
  templateUrl: './view-selector.component.html',
  styleUrls: ['./view-selector.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ViewSelectorComponent implements OnInit {
  @Output() public updateSelectedView = new EventEmitter<{
    data: string;
    type: string;
    changeUrl: boolean;
  }>();

  public LIMIT_FOR_GLOBAL_CUSTOM_VIEWS = 50;
  public LIMIT_FOR_PRIVATE_CUSTOM_VIEWS = 5;

  public isLoading = false;
  public isEditor: boolean;
  public views: any[] = [];
  public refModel: Refmodel;
  public customViewsList$: Observable<ICustomView[]>;
  public selectedView: any;
  public allowCreateNewView: boolean;
  public selectedViewName: string;

  globalCustomViewsList$: Observable<ICustomView[]>;
  privateCustomViewsList$: Observable<ICustomView[]>;
  cleanUpViewsList$: Observable<ICustomView[]>;

  public selectViewPlaceholder = this.translate.instant('SEARCH.METADATA_VIEWS_ PLACEHOLDER');
  public showViewDetails: boolean;
  public description: string;
  public fullText: boolean;
  public viewTypeLbl: string = '';

  private allowGlobalViewCreate: boolean;
  private allowPrivateViewCreate: boolean;
  private pendingCreateViewModal: boolean;

  constructor(
    private rmeStore: RefModelStoreService,
    private dialogService: EditorInterfaceDialogService,
    private customViewService: CustomViewService,
    private gridPager: GridPagerService,
    private gridSelectorService: GridSelectorService,
    private snackBarService: SnackBarService,
    private changeDetectorRef: ChangeDetectorRef,
    private customViewDaoService: CustomViewDaoService,
    private translate: TranslateService,
    private intercomEventService: IntercomEventService,
    private readonly route: ActivatedRoute,
    private gridStore: GridStore
  ) {}

  ngOnInit(): void {
    this.refModel = this.rmeStore.refModel;

    this.views = [TreeListViewEnum.ALL, TreeListViewEnum.MY_METADATA];
    // Org model access, not the space role — the server authorises custom view
    // writes from refModel.access (see checkIfUserCanManageViews).
    this.isEditor = this.rmeStore.isEditor;

    if (this.refModel.sourceType === 'salesforce') {
      this.getCustomViews();
    }
    this.subscribeToSelectedView();

    this.selectedViewName = this.selectViewPlaceholder;
    this.views.push(this.selectViewPlaceholder);

    if (this.route.snapshot.queryParams['create-view']) {
      this.pendingCreateViewModal = true;
    }
  }

  subscribeToSelectedView() {
    this.customViewService
      .subscribeOnSelectedView()
      .subscribe((selectedView: ICustomView | string) => {
        if (typeof selectedView !== 'string') {
          if (selectedView.system) {
            this.views.push(selectedView);
          }
          this.selectedView = selectedView as ICustomView;
          if (this.selectedView.private || this.selectedView.cleanUpView) {
            this.viewTypeLbl = `RUBIC_CUBE.${this.selectedView.private ? 'PRIVATE_VIEW_LBL' : 'SYSTEM_VIEW_LBL'}`;
          } else {
            this.viewTypeLbl = 'RUBIC_CUBE.SHARED_VIEW_LBL';
          }
          this.showViewDetails = true;
          this.selectedViewName = selectedView.name;
          this.resetCustomGridPagination();
        } else {
          this.selectedView = selectedView as string;
          this.selectedViewName = selectedView as string;
          this.showViewDetails = false;
          this.viewTypeLbl = '';
        }
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  showHideFullDescription($event: Event) {
    $event.preventDefault();
    if (this.fullText) {
      this.fullText = false;
      return;
    }
    this.description = this.selectedView.description;
    this.fullText = true;
  }

  getCustomViews() {
    this.isLoading = true;
    this.customViewService
      .subscribeOnCustomViewList()
      .pipe(
        tap((list: ICustomView[]) => {
          if (list) {
            /** Should filter by global and private views */
            const globalCustomViews = list.filter(view => !view.private && !view.cleanUpView);
            const privateCustomViews = list.filter(view => view.private && !view.cleanUpView);
            const cleanUpViews = list.filter(view => view.cleanUpView);
            /** Check if user hits the limits for global and private views */
            this.allowGlobalViewCreate =
              this.isEditor && globalCustomViews.length < this.LIMIT_FOR_GLOBAL_CUSTOM_VIEWS;
            this.allowPrivateViewCreate =
              privateCustomViews.length < this.LIMIT_FOR_PRIVATE_CUSTOM_VIEWS;
            /** Enable button if one of types allowed to create*/
            this.allowCreateNewView = this.allowGlobalViewCreate || this.allowPrivateViewCreate;

            this.globalCustomViewsList$ = of(globalCustomViews);
            this.privateCustomViewsList$ = of(privateCustomViews);
            this.cleanUpViewsList$ = of(cleanUpViews);

            if (this.pendingCreateViewModal) {
              this.createNewViewModal();
            }

            this.views = [...this.views, ...list];
          }

          if (this.pendingCreateViewModal) {
            this.pendingCreateViewModal = false;
          }

          this.isLoading = false;
        })
      )
      .subscribe();
  }

  switchTo(view: IDataTypeChangeEvent) {
    this.isLoading = true;
    this.gridStore.update({filters: []});
    this.gridStore.update({sort: []});
    this.updateSelectedView.emit({
      data: view['_id'] || view,
      type: view['private'] ? 'private' : 'global',
      changeUrl: true
    });
  }

  public createNewViewModal(): void {
    this.intercomEventService.sendIntercomEvent(
      this.refModel,
      IntercomEventsByArea.SALESFORCE_METADATA.METADATA_VIEW_CREATE_CLICKED
    );

    this.dialogService
      .openCreateNewViewDialog(this.refModel._id, {
        allowGlobalViewCreate: this.allowGlobalViewCreate,
        allowPrivateViewCreate: this.allowPrivateViewCreate
      })
      .afterClosed()
      .subscribe(emit => {
        const {view, msg} = emit || {};

        if (msg) {
          this.snackBarService.info(emit.msg);
        }

        if (view) {
          this.intercomEventService.sendIntercomEvent(
            this.refModel,
            IntercomEventsByArea.SALESFORCE_METADATA.METADATA_VIEW_CREATED
          );
          this.switchTo(emit.view);
        }
      });
  }

  public editMetadata(viewId: string) {
    this.dialogService
      .openEditCustomViewMetadataDialog(viewId)
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(response => this.modalWindowCloseHandler(response));
  }

  public remove(view: ICustomView) {
    this.dialogService
      .openDeleteDialog(view.name)
      .afterClosed()
      .pipe(
        switchMap(confirm => {
          if (confirm) {
            return this.customViewDaoService.removeCustomView(view.refModel, view._id);
          } else {
            return of(null);
          }
        })
      )
      .subscribe(
        response => {
          if (response) {
            this.customViewService.removeCustomView(view);

            const msg = this.translate.instant('RUBIC_CUBE.DELETED_SUCCESSFULLY', {
              name: view.name
            });

            this.snackBarService.success(msg);

            this.changeDetectorRef.detectChanges();
          }
        },
        error => {
          const msg = this.translate.instant(error.error.error || 'RUBIC_CUBE.DELETED_FAILURE', {
            name: view.name
          });

          this.snackBarService.error(msg);
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  private resetCustomGridPagination() {
    this.gridSelectorService.resetSelectedKeys();
    this.gridPager.resetPageIndex();
  }

  private modalWindowCloseHandler(response: any) {
    if (response?.data?._id === this.selectedView?._id) {
      this.updateSelectedView.emit({
        data: response.data['_id'],
        type: response.data['private'] ? 'private' : 'global',
        changeUrl: false
      });
    }

    this.snackBarService.info(response.msg);
    this.changeDetectorRef.detectChanges();
  }

  public switchToCleanUpView(systemView: IDataTypeChangeEvent) {
    if (this.selectedView._id && systemView['_id'] === this.selectedView._id) {
      return;
    }

    this.isLoading = true;
    this.updateSelectedView.emit({data: systemView['_id'], type: 'qc', changeUrl: true});
  }
}
